import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiPlus, FiTrash2, FiX, FiCheck, FiRefreshCw, FiEdit3, FiSearch, FiZap, FiUpload, FiUploadCloud } from 'react-icons/fi';
import { getStatus, getAgentSkills, getSkillLibrary, getPremadeSkills, assignSkill, removeSkill, assignPremadeToAgent, deleteSkillFromLibrary, createSkillFromText, updateSkillFromText, createSkillInLibrary, getSkillContent, listDwAgents, type AgentSkillRecord, type SkillLibraryItem, type PremadeSkillRecord, } from './claudeAgentApi';
import ClaudeAgentRequestPage from './ClaudeAgentRequestPage';
import ClaudeAgentPendingPage from './ClaudeAgentPendingPage';
import { useToast } from '../../context/ToastContext';
import SkillEditorBody from '../../components/SkillEditorBody';
function skillColor(name: string): string {
    const n = name.toLowerCase();
    if (n === 'xlsx')
        return '#22c55e';
    if (n === 'pptx')
        return '#f97316';
    if (n === 'pdf')
        return '#ef4444';
    if (n === 'docx')
        return '#3b82f6';
    return '#a78bfa';
}
const SkillIcon: React.FC<{
    name: string;
    size?: number;
}> = ({ name, size = 32 }) => {
    const color = skillColor(name);
    return (<div style={{
            width: size, height: size, borderRadius: 8, flexShrink: 0,
            background: `${color}18`, border: `1.5px solid ${color}35`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: "'SF Mono','Fira Code',monospace",
            fontSize: size < 30 ? 8 : 9, fontWeight: 700, color, letterSpacing: '-0.02em',
        }}>
      {name.slice(0, 4).toUpperCase()}
    </div>);
};
const wait = (ms: number) => new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
});
function formatSkillVersion(raw: string): string {
    if (!raw)
        return '';
    if (/^\d{8,}$/.test(raw))
        return '';
    return raw.length > 14 ? raw.slice(0, 12) + '…' : raw;
}
type FilterTab = 'all' | 'assigned' | 'available' | 'premade';
type RightPanel = {
    mode: 'empty';
} | {
    mode: 'create';
} | {
    mode: 'upload';
} | {
    mode: 'edit-library';
    skill: SkillLibraryItem;
} | {
    mode: 'view-assigned';
    skill: AgentSkillRecord;
} | {
    mode: 'view-premade';
    skill: PremadeSkillRecord;
};
const ClaudeAgentSkillsPage: React.FC = () => {
    const [agentId, setAgentId] = useState<string | null>(null);
    const [loadingAgent, setLoadingAgent] = useState(true);
    const [configured, setConfigured] = useState<boolean | null>(null);
    const [localGate, setLocalGate] = useState<'none' | 'pending'>('none');
    const [assigned, setAssigned] = useState<AgentSkillRecord[]>([]);
    const [library, setLibrary] = useState<SkillLibraryItem[]>([]);
    const [premade, setPremade] = useState<PremadeSkillRecord[]>([]);
    const [dataReady, setDataReady] = useState(false);
    const [filter, setFilter] = useState<FilterTab>('all');
    const [search, setSearch] = useState('');
    const [panel, setPanel] = useState<RightPanel>({ mode: 'create' });
    const [skillName, setSkillName] = useState('');
    const [skillBody, setSkillBody] = useState('');
    const [skillBodyLoading, setSkillBodyLoading] = useState(false);
    const [skillBodyHydratingId, setSkillBodyHydratingId] = useState<string | null>(null);
    const [editorMode, setEditorMode] = useState<'edit' | 'preview'>('edit');
    const [skillSaving, setSkillSaving] = useState(false);
    const [premadeReadonly, setPremadeReadonly] = useState(true);
    const { showError } = useToast();
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const [removing, setRemoving] = useState<number | null>(null);
    const [confirmRm, setConfirmRm] = useState<number | null>(null);
    const [adding, setAdding] = useState<string | null>(null);
    const [assigning, setAssigning] = useState<number | null>(null);
    const [creatingCopyFor, setCreatingCopyFor] = useState<number | null>(null);
    const [confirmCustomize, setConfirmCustomize] = useState(false);
    const [deletingLib, setDeletingLib] = useState<string | null>(null);
    const [confirmDeleteLib, setConfirmDeleteLib] = useState<string | null>(null);
    const textEditorRef = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
        setLoadingAgent(true);
        getStatus()
            .then(async (st) => {
            setConfigured(st.configured ?? false);
            if (!st.configured)
                return;
            const agents = await listDwAgents().catch(() => []);
            if (agents[0]?.agent_id) {
                setAgentId(agents[0].agent_id);
            }
        })
            .catch(() => { setConfigured(false); })
            .finally(() => setLoadingAgent(false));
    }, []);
    useEffect(() => {
        if (!agentId)
            return;
        Promise.all([
            getAgentSkills(agentId).catch(() => [] as AgentSkillRecord[]),
            getSkillLibrary().catch(() => ({ anthropic: [], custom: [] })),
            getPremadeSkills().catch(() => [] as PremadeSkillRecord[]),
        ]).then(([sk, lib, pm]) => {
            setAssigned(sk);
            setLibrary([...lib.anthropic, ...lib.custom]);
            setPremade(pm);
            setDataReady(true);
        });
    }, [agentId]);
    const assignedNames = new Set(assigned.map(s => s.name));
    const unassigned = library.filter(s => !assignedNames.has(s.name));
    function matchSearch(name: string, desc?: string) {
        if (!search.trim())
            return true;
        const q = search.toLowerCase();
        return name.toLowerCase().includes(q) || (desc ?? '').toLowerCase().includes(q);
    }
    const filteredAssigned = assigned.filter(s => matchSearch(s.name));
    const filteredUnassigned = unassigned.filter(s => matchSearch(s.name));
    const filteredPremade = premade.filter(p => matchSearch(p.title, p.description));
    const openCreate = () => {
        setSkillName('');
        setSkillBody('');
        setEditorMode('edit');
        setPremadeReadonly(false);
        setPanel({ mode: 'create' });
    };
    const openPremadeInEditor = (p: PremadeSkillRecord) => {
        setSkillName(p.title);
        setSkillBody(p.content);
        setEditorMode('edit');
        setPremadeReadonly(true);
        setConfirmCustomize(false);
        setPanel({ mode: 'create' });
    };
    const openEditLibrary = async (s: SkillLibraryItem) => {
        setSkillName(s.name);
        setSkillBody('');
        setEditorMode('edit');
        setSkillBodyLoading(s.source === 'custom');
        setSkillBodyHydratingId(s.source === 'custom' ? s.id : null);
        setPanel({ mode: 'edit-library', skill: s });
        if (s.source === 'custom') {
            const loadingStart = Date.now();
            try {
                const res = await getSkillContent(s.id);
                setSkillBody(res.content ?? '');
            }
            catch { }
            finally {
                const elapsed = Date.now() - loadingStart;
                if (elapsed < 350)
                    await wait(350 - elapsed);
                setSkillBodyLoading(false);
                setSkillBodyHydratingId(null);
            }
        }
        else {
            setSkillBodyLoading(false);
            setSkillBodyHydratingId(null);
        }
        setTimeout(() => textEditorRef.current?.focus(), 100);
    };
    const handleCreateSkill = async () => {
        if (!agentId || !skillName.trim() || !skillBody.trim())
            return;
        setSkillSaving(true);
        try {
            const newItem = await createSkillFromText(skillName.trim(), skillBody.trim(), false);
            const updated = await getSkillLibrary();
            setLibrary([...updated.anthropic, ...updated.custom]);
            await assignSkill(agentId, newItem);
            const sk = await getAgentSkills(agentId);
            setAssigned(sk);
            setSkillName('');
            setSkillBody('');
            setPanel({ mode: 'empty' });
        }
        catch (e: any) {
            showError(e.message ?? 'Failed to create skill');
        }
        finally {
            setSkillSaving(false);
        }
    };
    const handleSaveAndAssignPremade = async (premadeId?: number) => {
        if (!agentId || !skillName.trim() || !skillBody.trim())
            return;
        setSkillSaving(true);
        try {
            const v1Skill = await createSkillFromText(skillName.trim(), skillBody.trim(), false, premadeId);
            const updated = await getSkillLibrary();
            setLibrary([...updated.anthropic, ...updated.custom]);
            await assignSkill(agentId, v1Skill);
            setAssigned(await getAgentSkills(agentId));
            setSkillName('');
            setSkillBody('');
            setPanel({ mode: 'empty' });
        }
        catch (e: any) {
            showError(e.message ?? 'Failed to save and assign skill');
        }
        finally {
            setSkillSaving(false);
        }
    };
    const handleSavePremadeOnly = async (premadeId?: number) => {
        if (!skillName.trim() || !skillBody.trim())
            return;
        setSkillSaving(true);
        try {
            await createSkillFromText(skillName.trim(), skillBody.trim(), false, premadeId);
            const updated = await getSkillLibrary();
            setLibrary([...updated.anthropic, ...updated.custom]);
            setPremadeReadonly(true);
        }
        catch (e: any) {
            showError(e.message ?? 'Failed to save skill');
        }
        finally {
            setSkillSaving(false);
        }
    };
    const handleUpdateSkill = async () => {
        if (panel.mode !== 'edit-library')
            return;
        if (!agentId || !skillName.trim() || !skillBody.trim())
            return;
        setSkillSaving(true);
        try {
            const newItem = await updateSkillFromText(panel.skill.id, skillName.trim(), skillBody.trim(), false);
            const updated = await getSkillLibrary();
            setLibrary([...updated.anthropic, ...updated.custom]);
            if (assignedNames.has(panel.skill.name)) {
                const sk = await getAgentSkills(agentId);
                setAssigned(sk);
            }
            setPanel({ mode: 'edit-library', skill: newItem });
        }
        catch (e: any) {
            showError(e.message ?? 'Failed to update skill');
        }
        finally {
            setSkillSaving(false);
        }
    };
    const handleAddSkill = async (s: SkillLibraryItem) => {
        if (!agentId || adding)
            return;
        setAdding(s.id);
        try {
            await assignSkill(agentId, s);
            setAssigned(await getAgentSkills(agentId));
        }
        catch { }
        finally {
            setAdding(null);
        }
    };
    const handleRemoveSkill = async (id: number) => {
        if (!agentId)
            return;
        setRemoving(id);
        try {
            await removeSkill(agentId, id);
            setAssigned(prev => prev.filter(s => s.id !== id));
            if (panel.mode === 'view-assigned' && panel.skill.id === id)
                setPanel({ mode: 'empty' });
        }
        catch { }
        finally {
            setRemoving(null);
            setConfirmRm(null);
        }
    };
    const handleAssignPremade = async (p: PremadeSkillRecord) => {
        if (!agentId || assigning)
            return;
        setAssigning(p.id);
        try {
            await assignPremadeToAgent(agentId, p.id);
            setAssigned(await getAgentSkills(agentId));
        }
        catch { }
        finally {
            setAssigning(null);
        }
    };
    const handleEditAssignedPremade = async (assignedSkill: AgentSkillRecord, p: PremadeSkillRecord) => {
        if (!agentId || creatingCopyFor)
            return;
        setCreatingCopyFor(p.id);
        try {
            const v1Skill = await createSkillFromText(p.title, p.content, false, p.id);
            const updated = await getSkillLibrary();
            setLibrary([...updated.anthropic, ...updated.custom]);
            await removeSkill(agentId, assignedSkill.id);
            await assignSkill(agentId, v1Skill);
            setAssigned(await getAgentSkills(agentId));
            openEditLibrary(v1Skill);
        }
        catch { }
        finally {
            setCreatingCopyFor(null);
        }
    };
    const handleDeleteLibrarySkill = async (skillId: string) => {
        setDeletingLib(skillId);
        try {
            await deleteSkillFromLibrary(skillId);
            const updated = await getSkillLibrary();
            setLibrary([...updated.anthropic, ...updated.custom]);
            if (panel.mode === 'edit-library' && panel.skill.id === skillId)
                setPanel({ mode: 'empty' });
        }
        catch { }
        finally {
            setDeletingLib(null);
            setConfirmDeleteLib(null);
        }
    };
    const handleUploadSkill = async () => {
        if (!agentId || !uploadTitle.trim() || !uploadFile)
            return;
        setUploading(true);
        try {
            await createSkillInLibrary(uploadTitle.trim(), uploadFile, false);
            const updated = await getSkillLibrary();
            setLibrary([...updated.anthropic, ...updated.custom]);
            setPanel({ mode: 'empty' });
            setUploadTitle('');
            setUploadFile(null);
        }
        catch (e: any) {
            showError(e.message ?? 'Upload failed');
        }
        finally {
            setUploading(false);
        }
    };
    const insertMd = useCallback((before: string, after: string, linePrefix = '') => {
        const el = textEditorRef.current;
        if (!el)
            return;
        const { selectionStart: s, selectionEnd: e, value } = el;
        const selected = value.slice(s, e);
        const replacement = linePrefix
            ? `${linePrefix}${selected || 'text'}`
            : `${before}${selected || 'text'}${after}`;
        setSkillBody(value.slice(0, s) + replacement + value.slice(e));
        setTimeout(() => {
            const newPos = s + replacement.length;
            el.setSelectionRange(newPos, newPos);
            el.focus();
        }, 0);
    }, []);
    if (!loadingAgent && configured === false) {
        if (localGate === 'pending')
            return <ClaudeAgentPendingPage />;
        return (<ClaudeAgentRequestPage onActivated={() => window.location.reload()} onPending={() => setLocalGate('pending')}/>);
    }
    if (loadingAgent || !dataReady) {
        return (<div className="ca-fp-page">
        <div className="ca-fp-left">
          <div className="ca-fp-left__header">
            <div className="ca-fp-skel-bone" style={{ width: 80, height: 18, borderRadius: 6 }}/>
            <div className="ca-fp-skel-bone" style={{ width: 60, height: 28, borderRadius: 8 }}/>
          </div>
          <div className="ca-fp-left__search-wrap">
            <div className="ca-fp-skel-bone" style={{ height: 32, borderRadius: 8 }}/>
          </div>
          <div className="ca-fp-left__filters">
            {[80, 70, 80, 70].map((w, i) => <div key={i} className="ca-fp-skel-bone" style={{ width: w, height: 28, borderRadius: 8 }}/>)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '0 12px' }}>
            {[60, 75, 55, 70, 65].map((w, i) => (<div key={i} className="ca-fp-skill-row" style={{ pointerEvents: 'none' }}>
                <div className="ca-fp-skel-bone" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }}/>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div className="ca-fp-skel-bone" style={{ width: `${w}%`, height: 11 }}/>
                  <div className="ca-fp-skel-bone" style={{ width: '36%', height: 8, borderRadius: 4 }}/>
                </div>
              </div>))}
          </div>
        </div>
        <div className="ca-fp-right">
          <div className="ca-fp-editor">
            
            <div className="ca-fp-editor__topbar">
              <div className="ca-fp-editor__topbar-left">
                <div className="ca-fp-skel-bone" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }}/>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  <div className="ca-fp-skel-bone" style={{ width: '38%', height: 14, borderRadius: 6 }}/>
                  <div className="ca-fp-skel-bone" style={{ width: '22%', height: 9, borderRadius: 4 }}/>
                </div>
              </div>
              <div className="ca-fp-editor__topbar-actions">
                <div className="ca-fp-skel-bone" style={{ width: 60, height: 30, borderRadius: 8 }}/>
                <div className="ca-fp-skel-bone" style={{ width: 100, height: 30, borderRadius: 8 }}/>
              </div>
            </div>
            
            <div className="ca-fp-editor__body">
              <div className="ca-fp-editor__toolbar-row">
                <div className="ca-fp-skel-bone" style={{ width: 90, height: 28, borderRadius: 7 }}/>
                <div className="ca-fp-skel-bone" style={{ width: 180, height: 28, borderRadius: 7 }}/>
              </div>
              <div className="ca-fp-skel-bone" style={{ flex: 1, borderRadius: 11 }}/>
            </div>
          </div>
        </div>
      </div>);
    }
    const showAll = filter === 'all';
    const showAssigned = filter === 'all' || filter === 'assigned';
    const showAvailable = filter === 'all' || filter === 'available';
    const showPremadeTab = filter === 'all' || filter === 'premade';
    return (<div className="ca-fp-page">
      
      <div className="ca-fp-left">
        <div className="ca-fp-left__header">
          <h2 className="ca-fp-left__title">
            <FiZap size={15} style={{ color: '#f97316' }}/> Skills
            <span className="ca-fp-left__count">{assigned.length + unassigned.length + premade.length}</span>
          </h2>
          <div className="ca-fp-left__header-actions">
            <button className="ca-fp-create-btn" onClick={openCreate}>
              <FiPlus size={12}/> Create
            </button>
            <button className={`ca-fp-upload-hdr-btn${panel.mode === 'upload' ? ' ca-fp-upload-hdr-btn--active' : ''}`} onClick={() => { setPanel({ mode: 'upload' }); setUploadTitle(''); setUploadFile(null); }}>
              <FiUpload size={12}/> Upload
            </button>
          </div>
        </div>

        
        <div className="ca-fp-left__search-wrap">
          <FiSearch size={13} className="ca-fp-left__search-icon"/>
          <input className="ca-fp-left__search" placeholder="Search by name or tag…" value={search} onChange={e => setSearch(e.target.value)}/>
          {search && (<button className="ca-fp-left__search-clear" onClick={() => setSearch('')}>
              <FiX size={11}/>
            </button>)}
        </div>

        
        <div className="ca-fp-left__filters">
          {(['all', 'available', 'premade'] as FilterTab[]).map(f => (<button key={f} className={`ca-fp-filter-tab${filter === f ? ' ca-fp-filter-tab--active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'available' ? 'Anthropic' : f.charAt(0).toUpperCase() + f.slice(1)}
              <span className="ca-fp-filter-tab__badge">
                {f === 'all' ? assigned.length + unassigned.length + premade.length
                : f === 'assigned' ? assigned.length
                    : f === 'available' ? unassigned.length
                        : premade.length}
              </span>
            </button>))}
        </div>

        
        <div className="ca-fp-left__list">

          
          {showAssigned && filteredAssigned.length > 0 && (<>
              <div className="ca-fp-group-head">
                <span>Assigned</span>
                <span className="ca-fp-group-head__badge">{filteredAssigned.length}</span>
              </div>
              {filteredAssigned.map(s => {
                const libSkill = library.find(l => l.name === s.name);
                const isPremadeDirect = !!s.premade_id;
                const isActive = (panel.mode === 'view-assigned' && panel.skill.id === s.id)
                    || (panel.mode === 'edit-library' && panel.skill.name === s.name)
                    || (isPremadeDirect && panel.mode === 'create' && skillName === premade.find(p => p.id === s.premade_id)?.title && premadeReadonly);
                return (<div key={s.id} className={`ca-fp-skill-row ca-fp-skill-row--assigned${isActive ? ' ca-fp-skill-row--active' : ''}`} onClick={() => {
                        if (isPremadeDirect) {
                            const pm = premade.find(p => p.id === s.premade_id);
                            if (pm)
                                openPremadeInEditor(pm);
                            else
                                setPanel({ mode: 'view-assigned', skill: s });
                        }
                        else if (libSkill?.source === 'custom') {
                            openEditLibrary(libSkill);
                        }
                        else {
                            setPanel({ mode: 'view-assigned', skill: s });
                        }
                    }}>
                    <SkillIcon name={s.name} size={36}/>
                    <div className="ca-fp-skill-row__info">
                      <span className="ca-fp-skill-row__name">
                        {s.name}
                        {!isPremadeDirect && libSkill?.custom_version && (<span className="ca-fp-skill-row__name-ver"> {libSkill.custom_version}</span>)}
                      </span>
                      <span className="ca-fp-skill-row__ver">
                        <span className={`ca-fp-skill-row__src ca-fp-skill-row__src--${isPremadeDirect ? 'premade' : (libSkill?.source ?? 'custom')}`}>
                          {isPremadeDirect ? 'premade' : (libSkill ? libSkill.source : 'custom')}
                        </span>
                      </span>
                    </div>
                    <span className="ca-fp-skill-row__dot" title="Assigned & active"/>
                  </div>);
            })}
            </>)}

          
          {showAvailable && filteredUnassigned.length > 0 && (<>
              <div className="ca-fp-group-head">
                <span>Available in Anthropic</span>
                <span className="ca-fp-group-head__badge">{filteredUnassigned.length}</span>
              </div>
              {filteredUnassigned.map(s => {
                const isActive = panel.mode === 'edit-library' && panel.skill.id === s.id;
                const ver = s.source === 'custom' ? s.custom_version : null;
                const isDeleting = deletingLib === s.id;
                const confirmingDelete = confirmDeleteLib === s.id;
                return (<div key={s.id} className={`ca-fp-skill-row${isActive ? ' ca-fp-skill-row--active' : ''}`} onClick={() => { if (!confirmingDelete)
                    openEditLibrary(s); }}>
                    <SkillIcon name={s.name} size={36}/>
                    <div className="ca-fp-skill-row__info">
                      <span className="ca-fp-skill-row__name">
                        {s.name}
                        {ver && <span className="ca-fp-skill-row__name-ver"> {ver}</span>}
                      </span>
                      <span className="ca-fp-skill-row__ver">
                        <span className={`ca-fp-skill-row__src ca-fp-skill-row__src--${s.source}`}>{s.source}</span>
                      </span>
                    </div>
                    {s.source === 'custom' && (confirmingDelete ? (<div className="ca-fp-skill-row__del-confirm" onClick={e => e.stopPropagation()}>
                          <button className="ca-fp-skill-row__del-yes" onClick={() => handleDeleteLibrarySkill(s.id)} disabled={isDeleting}>
                            {isDeleting ? <FiRefreshCw size={10} className="ca-fp-spin"/> : 'Delete'}
                          </button>
                          <button className="ca-fp-skill-row__del-no" onClick={() => setConfirmDeleteLib(null)}>
                            Keep
                          </button>
                        </div>) : (<button className="ca-fp-skill-row__del-btn" title="Delete from library" onClick={e => { e.stopPropagation(); setConfirmDeleteLib(s.id); }}>
                          <FiTrash2 size={11}/>
                        </button>))}
                  </div>);
            })}
            </>)}

          
          {showPremadeTab && filteredPremade.length > 0 && (<>
              <div className="ca-fp-group-head">
                <span>Premade</span>
                <span className="ca-fp-group-head__badge">{filteredPremade.length}</span>
              </div>
              {filteredPremade.map(p => {
                const isActive = (panel.mode === 'view-premade' && panel.skill.id === p.id)
                    || (panel.mode === 'create' && skillName === p.title && premadeReadonly);
                const alreadyAssigned = assigned.some(s => s.premade_id === p.id) || assignedNames.has(`${p.title} v1`);
                return (<div key={p.id} className={`ca-fp-skill-row${isActive ? ' ca-fp-skill-row--active' : ''}${alreadyAssigned ? ' ca-fp-skill-row--assigned' : ''}`} onClick={() => openPremadeInEditor(p)}>
                    <SkillIcon name={p.title} size={36}/>
                    <div className="ca-fp-skill-row__info">
                      <span className="ca-fp-skill-row__name">{p.title}</span>
                      {p.description && <span className="ca-fp-skill-row__desc">{p.description}</span>}
                    </div>
                    {alreadyAssigned && <FiCheck size={11} style={{ color: '#22c55e', flexShrink: 0 }}/>}
                  </div>);
            })}
            </>)}

          
          {filteredAssigned.length === 0 && filteredUnassigned.length === 0 && filteredPremade.length === 0 && (<div className="ca-fp-left__empty">
              {search ? 'No skills match your search.' : 'No skills yet. Create one to get started.'}
            </div>)}
        </div>
      </div>

      
      <div className="ca-fp-right">

        
        {panel.mode === 'empty' && (<div className="ca-fp-empty-state">
            <div className="ca-fp-empty-state__icon">
              <FiZap size={32}/>
            </div>
            <h3>Select a skill to view or edit</h3>
            <p>Choose from the list on the left, or create a new skill using the button above.</p>
            <button className="ca-fp-create-btn ca-fp-create-btn--lg" onClick={openCreate}>
              <FiPlus size={14}/> Create New Skill
            </button>
          </div>)}

        
        {panel.mode === 'upload' && (<div className="ca-fp-editor">
            <div className="ca-fp-editor__topbar">
              <div className="ca-fp-editor__topbar-left">
                <span className="ca-fp-editor__badge ca-fp-editor__badge--new">Upload Skill</span>
                <span className="ca-fp-editor__skill-title" style={{ fontSize: 15 }}>Upload a .zip or .skill file</span>
              </div>
              <div className="ca-fp-editor__topbar-actions">
                <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => { setPanel({ mode: 'empty' }); setUploadTitle(''); setUploadFile(null); }}>
                  <FiX size={13}/> Cancel
                </button>
                <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleUploadSkill} disabled={uploading || !uploadTitle.trim() || !uploadFile}>
                  {uploading
                ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Uploading…</>
                : <><FiCheck size={12}/> Upload Skill</>}
                </button>
              </div>
            </div>
            <div className="ca-fp-upload-panel">
              <div className="ca-fp-upload-panel__field">
                <label className="ca-fp-upload-panel__label">Skill Title</label>
                <input className="ca-fp-upload-panel__input" placeholder="e.g. Quote Formatter" value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} autoFocus/>
              </div>
              <div className="ca-fp-upload-panel__field">
                <label className="ca-fp-upload-panel__label">File</label>
                <input ref={fileRef} type="file" accept=".zip,.skill" style={{ display: 'none' }} onChange={e => setUploadFile(e.target.files?.[0] ?? null)}/>
                <button className="ca-fp-upload-panel__dropzone" onClick={() => fileRef.current?.click()}>
                  <div className="ca-fp-upload-panel__dropzone-icon">
                    <FiUploadCloud size={26}/>
                  </div>
                  <div className="ca-fp-upload-panel__dropzone-text">
                    {uploadFile
                ? <><strong>{uploadFile.name}</strong><span>Click to change file</span></>
                : <><strong>Click to choose file</strong><span>Supports .zip and .skill formats</span></>}
                  </div>
                </button>
              </div>
              <p className="ca-fp-upload-panel__hint">
                The skill will be added to your library. You can assign it to your agent after uploading.
              </p>
            </div>
          </div>)}

        
        {panel.mode === 'create' && (() => {
            const isPremadePrefill = premade.some(p => p.title === skillName || `${p.title} v1` === skillName);
            const premadeItem = isPremadePrefill ? premade.find(p => p.title === skillName || `${p.title} v1` === skillName) : null;
            const alreadyAssigned = isPremadePrefill && (assignedNames.has(skillName) || !!(premadeItem && assignedNames.has(`${premadeItem.title} v1`)));
            const isAssigning = isPremadePrefill && premadeItem ? assigning === premadeItem.id : false;
            if (isPremadePrefill && premadeReadonly) {
                const premadeAssignedRecord = premadeItem
                    ? assigned.find(s => s.premade_id === premadeItem.id)
                    : undefined;
                const isCreatingCopy = premadeItem ? creatingCopyFor === premadeItem.id : false;
                return (<div className="ca-fp-editor">
                <div className="ca-fp-editor__topbar">
                  <div className="ca-fp-editor__topbar-left">
                    <SkillIcon name={skillName} size={36}/>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="ca-fp-editor__skill-title">{skillName}</span>
                        <span className="ca-fp-editor__badge ca-fp-editor__badge--premade">Premade</span>
                        {alreadyAssigned && <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Assigned</span>}
                      </div>
                      {premadeItem?.description && (<span className="ca-fp-editor__skill-sub">{premadeItem.description}</span>)}
                    </div>
                  </div>
                  <div className="ca-fp-editor__topbar-actions">
                    <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => { setPanel({ mode: 'empty' }); setSkillName(''); setSkillBody(''); setConfirmCustomize(false); }}>
                      <FiX size={13}/> Close
                    </button>
                    
                    {!confirmCustomize ? (<button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => {
                            if (premadeAssignedRecord) {
                                setConfirmCustomize(true);
                            }
                            else {
                                setPremadeReadonly(false);
                                setTimeout(() => textEditorRef.current?.focus(), 80);
                            }
                        }} disabled={isCreatingCopy}>
                        <FiEdit3 size={13}/> Customize
                      </button>) : (<>
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
                          Create "{skillName} v1"?
                        </span>
                        <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={() => premadeAssignedRecord && premadeItem && handleEditAssignedPremade(premadeAssignedRecord, premadeItem)} disabled={isCreatingCopy}>
                          {isCreatingCopy
                            ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Creating…</>
                            : <><FiCheck size={12}/> Yes, Customize</>}
                        </button>
                        <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setConfirmCustomize(false)} disabled={isCreatingCopy}>
                          <FiX size={12}/> Cancel
                        </button>
                      </>)}
                    {!confirmCustomize && (<button className="ca-fp-editor__btn ca-fp-editor__btn--assign" onClick={() => premadeItem && handleAssignPremade(premadeItem)} disabled={!!assigning || alreadyAssigned}>
                        {isAssigning
                            ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Assigning…</>
                            : alreadyAssigned
                                ? <><FiCheck size={12}/> Assigned</>
                                : <><FiPlus size={12}/> Assign to Agent</>}
                      </button>)}
                  </div>
                </div>
                <SkillEditorBody mode={editorMode} onModeChange={setEditorMode} body={skillBody} onBodyChange={setSkillBody} textEditorRef={textEditorRef} insertMd={insertMd} readOnly hint={premadeAssignedRecord
                        ? 'Org-provided skill — read-only. Click Customize to create your own editable copy (v1).'
                        : 'Org-provided skill — read-only. Click Customize to edit and save your own copy.'}/>
              </div>);
            }
            return (<div className="ca-fp-editor">
              <div className="ca-fp-editor__topbar">
                <div className="ca-fp-editor__topbar-left">
                  {isPremadePrefill ? (<>
                      <SkillIcon name={skillName} size={36}/>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="ca-fp-editor__skill-title">{skillName}</span>
                          <span className="ca-fp-editor__badge ca-fp-editor__badge--premade">Premade</span>
                        </div>
                        {premadeItem?.description && (<span className="ca-fp-editor__skill-sub">{premadeItem.description}</span>)}
                      </div>
                    </>) : (<>
                      <span className="ca-fp-editor__badge ca-fp-editor__badge--new">New Skill</span>
                      <input className="ca-fp-editor__name-input" placeholder="Skill name (e.g. Quote Formatter)" value={skillName} onChange={e => setSkillName(e.target.value)} autoFocus/>
                    </>)}
                </div>
                <div className="ca-fp-editor__topbar-actions">
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => {
                    if (isPremadePrefill) {
                        setPremadeReadonly(true);
                    }
                    else {
                        setPanel({ mode: 'empty' });
                        setSkillName('');
                        setSkillBody('');
                    }
                }}>
                    <FiX size={13}/> Cancel
                  </button>
                  {isPremadePrefill ? (<>
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => handleSavePremadeOnly(premadeItem?.id)} disabled={skillSaving || !skillName.trim() || !skillBody.trim()}>
                        {skillSaving
                        ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Saving…</>
                        : 'Save to Library'}
                      </button>
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={() => handleSaveAndAssignPremade(premadeItem?.id)} disabled={skillSaving || !skillName.trim() || !skillBody.trim()}>
                        {skillSaving
                        ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Saving…</>
                        : <><FiCheck size={12}/> Save &amp; Assign to Agent</>}
                      </button>
                    </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleCreateSkill} disabled={skillSaving || !skillName.trim() || !skillBody.trim()}>
                      {skillSaving
                        ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Saving…</>
                        : <><FiCheck size={12}/> Create & Assign</>}
                    </button>)}
                </div>
              </div>
              <SkillEditorBody mode={editorMode} onModeChange={setEditorMode} body={skillBody} onBodyChange={setSkillBody} textEditorRef={textEditorRef} insertMd={insertMd} hint={isPremadePrefill ? `Saving creates "${skillName} v1" as a custom skill in your library — the original premade is never modified.` : undefined}/>
            </div>);
        })()}

        
        {panel.mode === 'edit-library' && (() => {
            const s = panel.skill;
            const isAssigned = assignedNames.has(s.name);
            const assignedRecord = assigned.find(a => a.name === s.name);
            const isRemoving = assignedRecord ? removing === assignedRecord.id : false;
            return (<div className="ca-fp-editor">
              <div className="ca-fp-editor__topbar">
                <div className="ca-fp-editor__topbar-left">
                  <SkillIcon name={s.name} size={36}/>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="ca-fp-editor__skill-title">{s.name}</span>
                      <span className={`ca-fp-editor__badge ca-fp-editor__badge--${s.source}`}>{s.source}</span>
                      {isAssigned && <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Assigned</span>}
                    </div>
                    {(s.custom_version || (s.latest_version && !/^\d{8,}$/.test(s.latest_version))) && (<span className="ca-fp-editor__skill-sub">
                        {s.custom_version ?? `v${s.latest_version}`}
                      </span>)}
                  </div>
                </div>
                <div className="ca-fp-editor__topbar-actions">
                  {!isAssigned && s.source === 'custom' ? (<button className="ca-fp-editor__btn ca-fp-editor__btn--assign" onClick={() => handleAddSkill(s)} disabled={!!adding}>
                      {adding === s.id
                        ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Adding…</>
                        : <><FiPlus size={12}/> Assign to Agent</>}
                    </button>) : null}
                  {isAssigned && assignedRecord && (confirmRm === assignedRecord.id ? (<>
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Remove?</span>
                        <button className="ca-fp-editor__btn ca-fp-editor__btn--danger" onClick={() => handleRemoveSkill(assignedRecord.id)} disabled={isRemoving}>
                          {isRemoving ? <FiRefreshCw size={12} className="ca-fp-spin"/> : 'Yes, Remove'}
                        </button>
                        <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setConfirmRm(null)}>No</button>
                      </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" onClick={() => setConfirmRm(assignedRecord.id)}>
                        <FiTrash2 size={12}/> Remove
                      </button>))}
                  {s.source === 'custom' && (<button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleUpdateSkill} disabled={skillSaving || !skillName.trim() || !skillBody.trim()}>
                      {skillSaving
                        ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Saving…</>
                        : <><FiCheck size={12}/> Update Skill</>}
                    </button>)}
                </div>
              </div>

              {s.source === 'custom' ? (<SkillEditorBody mode={editorMode} onModeChange={setEditorMode} body={skillBody} onBodyChange={setSkillBody} loading={skillBodyLoading || skillBodyHydratingId === s.id} textEditorRef={textEditorRef} insertMd={insertMd} hint="Edit and save — updates the same skill in place (version increments automatically)."/>) : (<div className="ca-fp-anthropic-view">
                  
                  <div className="ca-fp-anthropic-view__banner">
                    <div className="ca-fp-anthropic-view__banner-icon">
                      <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
                        <g transform="translate(20,20)">
                          {[0, 45, 90, 135, 180, 225, 270, 315].map(a => (<rect key={a} x="-2" y="-14" width="4" height="9" rx="2" fill="rgba(249,115,22,0.85)" transform={`rotate(${a})`}/>))}
                        </g>
                      </svg>
                    </div>
                    <div className="ca-fp-anthropic-view__banner-text">
                      <span className="ca-fp-anthropic-view__banner-title">Anthropic-provided skill</span>
                      <span className="ca-fp-anthropic-view__banner-desc">
                        This skill is maintained by Anthropic Claude. Its content is managed externally and cannot be edited here.
                        You can assign it to your agent and it will be applied automatically.
                      </span>
                    </div>
                  </div>

                  
                  <div className="ca-fp-anthropic-view__meta-grid">
                    <div className="ca-fp-anthropic-view__meta-card">
                      <span className="ca-fp-anthropic-view__meta-label">Version</span>
                      <span className="ca-fp-anthropic-view__meta-val">{s.latest_version}</span>
                    </div>
                    <div className="ca-fp-anthropic-view__meta-card">
                      <span className="ca-fp-anthropic-view__meta-label">Source</span>
                      <span className="ca-fp-anthropic-view__meta-val ca-fp-anthropic-view__meta-val--orange">Anthropic</span>
                    </div>
                    <div className="ca-fp-anthropic-view__meta-card">
                      <span className="ca-fp-anthropic-view__meta-label">Created</span>
                      <span className="ca-fp-anthropic-view__meta-val">{new Date(s.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="ca-fp-anthropic-view__meta-card">
                      <span className="ca-fp-anthropic-view__meta-label">Status</span>
                      <span className={`ca-fp-anthropic-view__meta-val ${isAssigned ? 'ca-fp-anthropic-view__meta-val--green' : 'ca-fp-anthropic-view__meta-val--muted'}`}>
                        {isAssigned ? 'Assigned to agent' : 'Not assigned'}
                      </span>
                    </div>
                  </div>

                  
                  {!isAssigned && (<div className="ca-fp-anthropic-view__cta">
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--assign ca-fp-anthropic-view__cta-btn" onClick={() => handleAddSkill(s)} disabled={!!adding}>
                        {adding === s.id
                            ? <><FiRefreshCw size={13} className="ca-fp-spin"/> Assigning…</>
                            : <><FiPlus size={13}/> Assign to Agent</>}
                      </button>
                      <span className="ca-fp-anthropic-view__cta-hint">
                        The skill will be added to your agent's active skill set.
                      </span>
                    </div>)}

                  {isAssigned && (<div className="ca-fp-anthropic-view__assigned-state">
                      <FiCheck size={14} style={{ color: '#22c55e' }}/>
                      <span>This skill is active and assigned to your agent.</span>
                    </div>)}
                </div>)}
            </div>);
        })()}

        
        {panel.mode === 'view-assigned' && (() => {
            const s = panel.skill;
            const isRemoving = removing === s.id;
            const libSkill = library.find(l => l.name === s.name);
            return (<div className="ca-fp-editor">
              <div className="ca-fp-editor__topbar">
                <div className="ca-fp-editor__topbar-left">
                  <SkillIcon name={s.name} size={36}/>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="ca-fp-editor__skill-title">{s.name}</span>
                      <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Assigned</span>
                    </div>
                    <span className="ca-fp-editor__skill-sub">{formatSkillVersion(s.skill_type) || 'custom'}</span>
                  </div>
                </div>
                <div className="ca-fp-editor__topbar-actions">
                  {libSkill?.source === 'custom' && (<button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => openEditLibrary(libSkill)}>
                      <FiEdit3 size={12}/> Edit
                    </button>)}
                  {confirmRm === s.id ? (<>
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Remove?</span>
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--danger" onClick={() => handleRemoveSkill(s.id)} disabled={isRemoving}>
                        {isRemoving ? <FiRefreshCw size={12} className="ca-fp-spin"/> : 'Yes, Remove'}
                      </button>
                      <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setConfirmRm(null)}>No</button>
                    </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" onClick={() => setConfirmRm(s.id)}>
                      <FiTrash2 size={12}/> Remove
                    </button>)}
                </div>
              </div>
              <div className="ca-fp-editor__readonly">
                <div className="ca-fp-editor__readonly-meta">
                  <span><strong>Status:</strong> Active &amp; Assigned</span>
                  <span><strong>Version:</strong> {formatSkillVersion(s.skill_type) || '—'}</span>
                  <span><strong>Added:</strong> {new Date(s.created_at).toLocaleDateString()}</span>
                </div>
                {libSkill?.source === 'custom' && (<p className="ca-fp-editor__readonly-msg" style={{ marginTop: 16 }}>
                    Click <strong>Edit</strong> to modify the skill's content.
                  </p>)}
              </div>
            </div>);
        })()}

        
        {panel.mode === 'view-premade' && (() => {
            const p = panel.skill;
            const alreadyAssigned = assignedNames.has(p.title);
            const isAssigning = assigning === p.id;
            return (<div className="ca-fp-editor">
              <div className="ca-fp-editor__topbar">
                <div className="ca-fp-editor__topbar-left">
                  <SkillIcon name={p.title} size={36}/>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="ca-fp-editor__skill-title">{p.title}</span>
                      <span className="ca-fp-editor__badge ca-fp-editor__badge--premade">Premade</span>
                      {alreadyAssigned && <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Assigned</span>}
                    </div>
                    {p.description && <span className="ca-fp-editor__skill-sub">{p.description}</span>}
                  </div>
                </div>
                <div className="ca-fp-editor__topbar-actions">
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--assign" onClick={() => handleAssignPremade(p)} disabled={!!assigning || alreadyAssigned}>
                    {isAssigning
                    ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Assigning…</>
                    : alreadyAssigned
                        ? <><FiCheck size={12}/> Assigned</>
                        : <><FiPlus size={12}/> Assign to Agent</>}
                  </button>
                </div>
              </div>

              
              <div className="ca-fp-editor__premade-preview">
                <div className="ca-fp-editor__premade-preview__label">Skill Content</div>
                <pre className="ca-fp-editor__premade-pre">{p.content}</pre>
              </div>
            </div>);
        })()}
      </div>

    </div>);
};
export default ClaudeAgentSkillsPage;
